// src/client/screens/borrower/LoanConfirmScreen.tsx
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { SUPPORTED_MM_PROVIDERS, type MobileMoneyProvider } from '@/constants/config';
import { computeAllInCost } from '@client/utils/loanUtils';
import { applyLoan } from '@client/services/loanService';
import { readLoanDraft, clearLoanDraft, type LoanDraft } from '@client/lib/loanDraft';
import { type Ngwee } from '@/lib/types';
import { useAuth } from '@client/auth/useAuth';
import { supabase } from '@client/lib/supabase';
import ScreenShell from '@client/components/ScreenShell';
import Card from '@client/components/Card';
import CostBreakdown from '@client/components/CostBreakdown';
import Button from '@client/components/Button';
import PaymentMethodPicker from '@client/components/PaymentMethodPicker';
import { useToast } from '@client/components/ToastProvider';

// "+260971234567" → "+260 97 *** 4567"
function maskPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10) return phone;
  const country = digits.slice(0, 3);
  const prefix  = digits.slice(3, 5);
  const last4   = digits.slice(-4);
  return `+${country} ${prefix} *** ${last4}`;
}

// Looks up an existing payment method for the given provider or creates one
// using the borrower's profile phone. Stored plain in stub mode; encrypt
// full_number_encrypted before switching to a real mobile-money aggregator.
async function getOrCreatePaymentMethod(
  provider: MobileMoneyProvider,
  phone: string,
  profileId: string,
): Promise<string> {
  // Return an existing (non-deleted) method for this provider first.
  const { data: existing } = await supabase
    .from('payment_methods')
    .select('id')
    .eq('user_id', profileId)
    .eq('provider', provider)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();

  if (existing) return existing.id as string;

  // The unique index allows only one default per user, so only mark as
  // default if the user has no existing methods at all.
  const { data: anyMethod } = await supabase
    .from('payment_methods')
    .select('id')
    .eq('user_id', profileId)
    .is('deleted_at', null)
    .limit(1)
    .maybeSingle();

  const { data: created, error } = await supabase
    .from('payment_methods')
    .insert({
      user_id: profileId,
      provider,
      masked_number: maskPhone(phone),
      full_number_encrypted: phone, // plaintext in stub mode
      is_default: !anyMethod,
    })
    .select('id')
    .single();

  if (error || !created) throw new Error('Failed to create payment method');
  return created.id as string;
}

export default function LoanConfirmScreen() {
  const [draft, setDraft] = useState<LoanDraft | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [provider, setProvider] = useState<MobileMoneyProvider>(SUPPORTED_MM_PROVIDERS[0]);
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useAuth();

  useEffect(() => {
    const d = readLoanDraft();
    if (!d) {
      navigate(ROUTES.loanApply, { replace: true });
      return;
    }
    setDraft(d);
  }, [navigate]);

  if (!draft) return null;

  const cost = computeAllInCost(draft.principalNgwee as Ngwee, draft.termDays);

  const submit = async () => {
    if (!profile) {
      toast.show('Please sign in again', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const paymentMethodId = await getOrCreatePaymentMethod(provider, profile.phone, profile.id);
      const r = await applyLoan({
        principal: draft.principalNgwee as Ngwee,
        termDays: draft.termDays,
        paymentMethodId,
        purpose: draft.purpose,
      });
      if (!r.ok) {
        const msg =
          r.error === 'concurrent_loan'
            ? 'You already have an active loan'
            : r.error === 'cooling_off'
              ? 'Please wait 24 hours after repayment before applying again'
              : r.error === 'kyc_not_verified'
                ? 'Your KYC verification is not complete'
                : 'Could not submit application';
        toast.show(msg, 'error');
        return;
      }
      clearLoanDraft();
      navigate(ROUTES.loanSuccess, { replace: true });
    } catch {
      toast.show('Could not submit application', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ScreenShell title="Confirm" back>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card title="You're applying for">
          <CostBreakdown cost={cost} />
          <p className="small" style={{ marginTop: 12 }}>
            Term: {draft.termDays} days. Disbursed to your registered phone.
          </p>
        </Card>

        <Card title="Receive funds via">
          <PaymentMethodPicker value={provider} onChange={setProvider} />
          {profile && (
            <p className="small" style={{ marginTop: 10, color: 'var(--c-text-muted)' }}>
              Disbursement to {maskPhone(profile.phone)}
            </p>
          )}
        </Card>

        <Card title="Loan agreement">
          <p className="small">
            By tapping <strong>Accept &amp; submit</strong>, you agree to repay
            the full amount above on or before the due date. Late payments
            incur a daily fee. No prepayment penalty.
          </p>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
            />
            <span>I accept the agreement</span>
          </label>
        </Card>

        <Button
          block size="lg" loading={submitting}
          disabled={!accepted}
          onClick={submit}
        >
          Accept &amp; submit
        </Button>
      </div>
    </ScreenShell>
  );
}
